import { atom } from 'jotai';
import { atomWithReset, atomWithStorage } from 'jotai/utils';
import { verifyAccessToken } from 'lib/auth';
import axios from 'lib/axiosInstance';
import { showFailureToast } from 'lib/ToastHelper';
import { companyIdAtom, HostActiveStatusFilterType } from 'views/state';
import { DEFAULT_PAGE_SIZE } from 'views/types';

import { loadCloudAgentsData } from './helper';
import { caRefreshAtom } from '../state';

export const serverOrWorkstationFilterAtom = atomWithReset<string>('');
export const selectedCloudAgentsFilterAtom =
  atomWithReset<HostActiveStatusFilterType>('active');
export const searchPhraseFilterAtom = atomWithReset<string>('');

export const checkedListAtom = atomWithReset<number[]>([]);

export const forceRerenderUIAtom = atom<string>('');

export const isCheckAllAtom = atomWithReset(false);

export const DEFAULT_ACTIVE_CA_LISTING_COLUMNS = [
  'name_with_type',
  'alias',
  'address',
  'os',
  'total_vuln',
  'last_vuln_scan',
  'last_checked_in',
  'actions',
];
export const activeCAColumnKeysAtom = atomWithStorage(
  'ca_list_active_column_keys',
  DEFAULT_ACTIVE_CA_LISTING_COLUMNS,
);

export const DEFAULT_AGED_OUT_CA_LISTING_COLUMNS = [
  'name_with_type',
  'alias',
  'address',
  'os',
  'deleted_cloudagent__open_vuln_counts',
  'deleted_cloudagent__closed_vuln_counts',
  'deleted_cloudagent__deleted_at',
  'deleted_cloudagent__deleted_by',
  'actions',
];
export const agedOutCAColumnKeysAtom = atomWithStorage(
  'ca_list_aged_out_column_keys',
  DEFAULT_AGED_OUT_CA_LISTING_COLUMNS,
);
// export const appliedRowIdsAtom = atomWithReset<number[]>([]);

export const paginationConfigAtom = atomWithStorage(
  'ca_dashboard_pagination_config',
  {
    page_size: DEFAULT_PAGE_SIZE,
    page: 1,
  },
);
// export const orderbyAtom = atomWithReset<string>("-total_vuln");
export const orderByAtom = atomWithReset<string>('total_vuln');
export const sortOrderAtom = atomWithReset<'desc' | 'asc'>('desc');
export const orderingAtom = atom(async (get) => {
  const orderBy = get(orderByAtom);
  const order = get(sortOrderAtom);
  return `${order === 'desc' ? '-' : ''}${orderBy}`;
});

// component height
export const componentHeightAtom = atom(0);

// Add a new atom to store vulnerability counts for checked hosts
export const checkedHostsVulnMapAtom = atom<Record<number, number>>({});

export const vulnCountForCheckedHostsAtom = atom(async (get) => {
  const checkedHostIds = get(checkedListAtom);
  const vulnMap = get(checkedHostsVulnMapAtom);
  let total = 0;

  if (checkedHostIds?.length > 0) {
    const allHosts = get(caListingDataAtom);

    // Update vulnMap with any new hosts from current page
    const currentPageHosts = allHosts?.items?.filter((host) =>
      checkedHostIds.includes(host.id),
    );

    // Calculate total using both stored and current page values
    checkedHostIds.forEach((hostId) => {
      // Use stored value if available, otherwise 0
      total += vulnMap[hostId] || 0;
    });

    // Update totals with any new values from current page
    currentPageHosts?.forEach((host) => {
      if (host.total_vuln !== vulnMap[host.id]) {
        total = total - (vulnMap[host.id] || 0) + (host.total_vuln || 0);
      }
    });
  }
  return total;
});

interface Insights {
  status_breakdown: {
    active: number;
    aging: number;
    deleted: number;
  };
  severity_breakdown: {
    urgent: number;
    critical: number;
    serious: number;
    medium: number;
    minimal: number;
  };
  servers_count: number;
  workstations_count: number;
  total_vulnerabilities: number;
  unique_os_count: number;
}

export const caListingDataAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const paginationConfig = get(paginationConfigAtom);
  const pageSize = paginationConfig.page_size;
  const currentPage = paginationConfig.page;

  const ordering = get(orderingAtom);
  const serverOrWorkstation = get(serverOrWorkstationFilterAtom);
  const selectedCloudAgentsFilter = get(selectedCloudAgentsFilterAtom);
  const searchPhrase = get(searchPhraseFilterAtom);
  const _ = get(caRefreshAtom);

  try {
    const responseData = await loadCloudAgentsData(
      companyId,
      pageSize,
      currentPage,
      ordering,
      serverOrWorkstation,
      searchPhrase,
      selectedCloudAgentsFilter,
    );
    return {
      items: responseData.items,
      count: responseData.count,
      insights: responseData.insights,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }

  return {
    items: undefined,
    count: 0,
    insights: {} as Insights,
  };
});

export const activationKeyOptionsAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const response = await getActivationKeys(companyId);
  return response;
});

async function getActivationKeys(companyId: number) {
  try {
    await verifyAccessToken();
    let url = '/cloud_agents/activation-keys/' + companyId;
    let headers = {
      Authorization: 'Bearer ' + localStorage.getItem('access'),
    };

    const res = await axios.get(url, { headers: headers });
    if (res.status === 201 || res.status === 200) {
      const data = res.data;
      return Object.entries(data)
        .filter(([key]) => key !== 'customerId') // Exclude customerId if not needed
        .map(([key, value]) => ({
          value: String(value),
          label: String(key).replace(/^./, (str) => str.toUpperCase()), // Format label
        }));
    }
  } catch (err) {
    showFailureToast('Error fetching activation keys:');
    return [];
  }
}
